/**
 * The browser's notification permission as the contract expects it:
 * `authorized | provisional | denied | unknown`. `Notification.permission` answers `granted`,
 * `denied` or `default` (never asked, or the user closed the prompt), which is `unknown`.
 */
export const toPermissionStatus = permission => {
  switch (permission) {
    case 'granted':
      return 'authorized';
    case 'denied':
      return 'denied';
    default:
      return 'unknown';
  }
};

/** Web push needs a service worker, the Push API and the Notifications API. */
export const isPushSupported = () =>
  typeof window !== 'undefined' &&
  'Notification' in window &&
  'PushManager' in window &&
  'serviceWorker' in navigator;

export const readPermissionStatus = async () => {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unknown';
  }
  return toPermissionStatus(window.Notification.permission);
};

/**
 * Asks the browser. Only from a user action (a button): browsers ignore or quiet a prompt that
 * arrives without one. `Notification.requestPermission` is called before anything is awaited so
 * the click that triggered it still counts. Older Safari answers through a callback only.
 */
export const requestPermission = async () => {
  if (!isPushSupported()) {
    return 'unknown';
  }
  try {
    const answer = await new Promise(resolve => {
      const promise = window.Notification.requestPermission(resolve);
      if (promise?.then) {
        promise.then(resolve, () => resolve(window.Notification.permission));
      }
    });
    return toPermissionStatus(answer);
  } catch (err) {
    console.warn('[NotificationsV2] permission request failed', err?.message);
    return readPermissionStatus();
  }
};

/**
 * Fires when the user changes the permission from the browser's site settings while the site is
 * open. Returns the unsubscribe function; a no-op where the Permissions API can't watch it.
 */
export const onPermissionChange = listener => {
  let status = null;
  let cancelled = false;

  try {
    navigator.permissions
      ?.query({ name: 'notifications' })
      .then(result => {
        if (cancelled) {
          return;
        }
        status = result;
        status.onchange = () => listener();
      })
      .catch(() => null);
  } catch {
    /* no Permissions API */
  }

  return () => {
    cancelled = true;
    if (status) {
      status.onchange = null;
    }
  };
};
